import { toInteger } from 'lodash';
import { createCanvas } from '@/utils/file-kit';
import { type IMediaLayerProps } from '@/version-types';
import { loadVideoCover } from '../utils/video';
import { ICON_VISIBILITY_THRESHOLD } from './constants';
import { type EdgelessMediaLayer } from './index';
import { type IEdgelessMediaLayer, type RenderTarget } from './types';

const VIDEO_IDENTIFIER_SIZE_RATIO = (45 * 3) / (176 * 4);
const VIDEO_PLAY_ICON_SIZE_RATIO = 20 / 40;
const VIDEO_FRAME_WAIT_TIMEOUT = 10_000;

export class EdgelessVideoElement implements IEdgelessMediaLayer {
    declare videoEl: HTMLVideoElement;
    declare private videoCanvas: HTMLCanvasElement;
    declare private videoCtx: CanvasRenderingContext2D;
    declare private videoCover: HTMLImageElement | HTMLCanvasElement;
    declare private videoCoverRaw: HTMLImageElement | HTMLCanvasElement;
    declare private videoPausedCanvas: HTMLCanvasElement;
    private renderTarget: RenderTarget = 'cover';
    private loopCallback: (() => void | boolean) | null = null;
    private cancelFrameWait: (() => void) | null = null;
    private disposed = false;
    private readonly endedHandler = () => this.onEnded();
    private readonly seekedHandler = () => this.onSeeked();

    constructor(private object: EdgelessMediaLayer) {
        const layer = this.object.service.flatLayer.getLayer(this.object.layerId);
        const props = layer.data as IMediaLayerProps;
        const { naturalWidth, naturalHeight, url, muted = false } = props;

        const videoEl = document.createElement('video');
        const id = `${this.object.layerId}-video`;

        videoEl.id = id;
        videoEl.width = naturalWidth;
        videoEl.height = naturalHeight;
        videoEl.muted = muted;

        // console.log('loadResource', url);

        const source = document.createElement('source');
        source.src = url;
        videoEl.appendChild(source);

        videoEl.preload = 'metadata';
        videoEl.load();

        videoEl.addEventListener('ended', this.endedHandler);
        videoEl.addEventListener('seeked', this.seekedHandler);

        this.videoEl = videoEl;
    }

    getDurationAndUrl() {
        const layer = this.object.service.flatLayer.getLayer(this.object.layerId);
        if (!layer) {
            return {
                duration: 0,
                url: '',
            };
        }
        const props = layer.data as IMediaLayerProps;
        const { url } = props;
        return {
            duration: this.videoEl?.duration ?? 0,
            url,
        };
    }

    async play() {
        const video = this.videoEl;
        if (!video || this.disposed) return;
        this.cancelPendingFrameWait();
        await video.play();
        if (video !== this.videoEl || video.paused || this.disposed) return;

        const hasFrame = await new Promise<boolean>((resolve, reject) => {
            let animationFrameId: number | null = null;
            let timeoutId: ReturnType<typeof setTimeout> | null = null;
            let settled = false;

            const finish = (ready: boolean, error?: Error) => {
                if (settled) return;
                settled = true;
                if (animationFrameId !== null) {
                    cancelAnimationFrame(animationFrameId);
                }
                if (timeoutId !== null) {
                    clearTimeout(timeoutId);
                }
                if (this.cancelFrameWait === cancel) {
                    this.cancelFrameWait = null;
                }
                if (error) {
                    reject(error);
                    return;
                }
                resolve(ready);
            };

            const cancel = () => finish(false);
            const checkFrame = () => {
                if (video !== this.videoEl || video.paused || this.disposed) {
                    finish(false);
                    return;
                }
                if (video.videoWidth > 0 && video.videoHeight > 0 && video.currentTime > 0) {
                    finish(true);
                } else {
                    animationFrameId = requestAnimationFrame(checkFrame);
                }
            };

            this.cancelFrameWait = cancel;
            timeoutId = setTimeout(() => finish(false, new Error('等待视频首帧超时')), VIDEO_FRAME_WAIT_TIMEOUT);
            checkFrame();
        });
        if (!hasFrame || video !== this.videoEl || video.paused || this.disposed) return;

        this.renderTarget = 'frame';
        this.resetResource();

        this.object.reportPlay();
    }

    pause() {
        if (!this.videoEl) return;

        this.videoEl.pause();
        this.cancelPendingFrameWait();
        if (this.renderTarget === 'frame') {
            this.resetResource();
            this.renderPausedIdentifier();
        }

        this.object.reportPause();
        this.object.service.edgelessCanvas.requestRenderAll();
    }

    reset() {
        if (!this.videoEl) return;
        this.videoEl.pause();
        this.cancelPendingFrameWait();
        this.seek(0);
        this.object.reportPause();
    }

    seek(time: number) {
        if (!this.videoEl) return;
        this.videoEl.currentTime = time;
    }

    mute(muted: boolean) {
        if (!this.videoEl) return;
        this.videoEl.muted = muted;
    }

    async loadResource(showIdentifier = true) {
        await this.loadCover(showIdentifier);

        this.resetResource();
    }

    /** 重新加载视频资源（URL 变化时调用） */
    async reloadResource() {
        this.cancelPendingFrameWait();
        const layer = this.object.service.flatLayer.getLayer(this.object.layerId);
        const props = layer.data as IMediaLayerProps;
        const { naturalWidth, naturalHeight, url, muted = false } = props;

        // 更新 video 元素尺寸和属性
        this.videoEl.width = naturalWidth;
        this.videoEl.height = naturalHeight;
        this.videoEl.muted = muted;

        // 更新 video source
        const source = this.videoEl.querySelector('source');
        if (source) {
            source.src = url;
        }
        this.videoEl.load();

        // 分辨率变化后，重置已有帧渲染缓冲，避免沿用旧尺寸 canvas
        if (this.videoCtx) {
            this.videoCtx = undefined!;
            this.videoCanvas = undefined!;
        }
        this.videoPausedCanvas = undefined!;

        // 清空封面缓存，强制重新加载
        this.videoCoverRaw = undefined!;
        this.videoCover = undefined!;
        this.setRenderCover();

        await this.loadResource();
    }

    setRenderCover() {
        this.renderTarget = 'cover';
    }

    /**
     * 读取视频封面，供导航预览使用，不影响视频进度或暂停画面
     * @returns 已加载的视频封面
     */
    getPreviewCover() {
        return this.videoCover;
    }

    resetResource() {
        if (this.renderTarget === 'cover') {
            this.object.resource = this.videoCover;
            return;
        }
        if (this.renderTarget === 'paused-frame') {
            this.object.resource = this.videoPausedCanvas;
            return;
        }
        if (this.object.__renderAiWatermark && !this.videoCtx) {
            const { canvas, ctx } = createCanvas(this.videoEl.width, this.videoEl.height);
            this.videoCanvas = canvas;
            this.videoCtx = ctx;
        }
        if (this.videoCtx) {
            this.videoCtx.clearRect(0, 0, this.videoCanvas.width, this.videoCanvas.height);
            this.videoCtx.drawImage(this.videoEl, 0, 0, this.videoEl.width, this.videoEl.height);
            this.object.drawAiWatermark(this.videoCanvas);
            this.object.resource = this.videoCanvas;
        } else {
            this.object.resource = this.videoEl;
        }
    }

    dispose() {
        this.disposed = true;
        this.videoEl.pause();
        this.cancelPendingFrameWait();
        this.videoEl.removeEventListener('ended', this.endedHandler);
        this.videoEl.removeEventListener('seeked', this.seekedHandler);
    }

    onLoop(cb: () => void | boolean) {
        this.loopCallback = cb;
    }

    private onEnded() {
        this.cancelPendingFrameWait();
        if (this.loopCallback?.()) {
            return;
        }
        this.setRenderCover();
        this.resetResource();
        this.object.reportPause();
    }

    private onSeeked() {
        this.renderTarget = 'frame';
        this.resetResource();

        if (this.videoEl.paused) {
            this.renderPausedIdentifier();
        }

        this.object.service.edgelessCanvas.requestRenderAll();
    }

    private cancelPendingFrameWait() {
        const cancel = this.cancelFrameWait;
        this.cancelFrameWait = null;
        cancel?.();
    }

    private renderPausedIdentifier() {
        const layer = this.object.service.flatLayer.getLayer(this.object.layerId);
        if (layer.width <= ICON_VISIBILITY_THRESHOLD || layer.height <= ICON_VISIBILITY_THRESHOLD) return;

        // 暂停帧最终会被拉伸到图层尺寸，因此画布也必须保持图层宽高比，避免播放按钮随视频原始比例变形。
        const scale = this.object.getResourceScale({
            naturalWidth: this.videoEl.width,
            naturalHeight: this.videoEl.height,
        });
        const width = toInteger(layer.width * scale);
        const height = toInteger(layer.height * scale);
        const source = this.videoCtx ? this.videoCanvas : this.videoEl;
        const { canvas, ctx } = createCanvas(width, height);
        ctx.drawImage(source, 0, 0, width, height);
        this.renderIdentifierIcon(ctx, width, height, VIDEO_IDENTIFIER_SIZE_RATIO * Math.min(width, height));
        this.videoPausedCanvas = canvas;
        this.renderTarget = 'paused-frame';
        this.object.resource = canvas;
    }

    private async loadCover(showIdentifier = true) {
        const layer = this.object.service.flatLayer.getLayer(this.object.layerId);
        const props = layer.data as IMediaLayerProps;

        try {
            if (!this.videoCoverRaw) {
                this.videoCoverRaw = await loadVideoCover(props.poster, props.url);
            }

            let cover: HTMLCanvasElement | HTMLImageElement = this.videoCoverRaw;

            if (layer.width > ICON_VISIBILITY_THRESHOLD && layer.height > ICON_VISIBILITY_THRESHOLD) {
                const scale = this.object.getResourceScale({
                    naturalWidth: cover.width,
                    naturalHeight: cover.height,
                });
                const width = toInteger(layer.width * scale);
                const height = toInteger(layer.height * scale);
                const { canvas, ctx } = createCanvas(width, height, cover);
                // 是否绘制视频标识
                if (showIdentifier) {
                    this.renderIdentifierIcon(
                        ctx,
                        canvas.width,
                        canvas.height,
                        VIDEO_IDENTIFIER_SIZE_RATIO * Math.min(width, height),
                    );
                }
                cover = canvas;
                await this.object.drawAiWatermarkAsync(cover);
            }

            this.videoCover = cover;
        } catch (e) {
            console.log('loadCover error', e, props.poster || '');
        }
    }

    private renderIdentifierIcon(
        ctx: CanvasRenderingContext2D,
        canvasWidth: number,
        canvasHeight: number,
        iconWidth: number,
    ) {
        const x = (canvasWidth - iconWidth) / 2;
        const y = (canvasHeight - iconWidth) / 2;
        const radius = iconWidth / 2;

        const { canvas: snapshot, ctx: snapshotCtx } = createCanvas(canvasWidth, canvasHeight);
        snapshotCtx.drawImage(ctx.canvas, 0, 0);

        ctx.save();
        ctx.beginPath();
        ctx.arc(x + radius, y + radius, radius, 0, Math.PI * 2);
        ctx.clip();
        ctx.filter = 'blur(5px)';
        ctx.drawImage(snapshot, 0, 0);
        ctx.restore();

        ctx.save();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
        ctx.beginPath();
        ctx.arc(x + radius, y + radius, radius, 0, Math.PI * 2);
        ctx.fill();

        const playSize = iconWidth * VIDEO_PLAY_ICON_SIZE_RATIO;
        const playX = x + (iconWidth - playSize) / 2;
        const playY = y + (iconWidth - playSize) / 2;

        ctx.fillStyle = '#FFFFFF';
        ctx.beginPath();
        ctx.moveTo(playX + playSize * 0.2188, playY + playSize * 0.1178);
        ctx.lineTo(playX + playSize * 0.9062, playY + playSize * 0.5);
        ctx.lineTo(playX + playSize * 0.2188, playY + playSize * 0.8822);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
    }
}
